import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadUrlRequest, UploadUrlResponse, ShareRequest, ShareResponse, FileDetailResponse, isValidEmail } from '@df-gate-share/shared';
import axios from 'axios';
import { apiClient, ERROR_MESSAGES } from '../lib/api';
import ContactsModal from '../components/ContactsModal';
import Header from '../components/Header';
import MessageDialog from '../components/MessageDialog';
import RecipientChip from '../components/RecipientChip';
import { useTrustCheck } from '../hooks/useTrustCheck';

// Bug-018: スキャンで共有不可と判定された理由。これがセットされたファイルは
// ファイル一覧で警告表示し、削除を促す。
type FileScanState = 'unsupported' | 'scan_failed' | 'infected';

interface UploadFile {
  file: File;
  progress: number;
  // アップロード後に割り当てられる fileId と、スキャンで問題があった場合の種別。
  fileId?: string;
  scanState?: FileScanState;
}

// Bug-019: 共有作成API(create-share)の上限と揃える。超過分は送信前にクライアント側で弾く。
const MAX_FILES = 10;

// Bug-018: ファイル詳細のスキャン結果から「共有不可」の種別を導出する。
const deriveScanState = (
  r?: { status?: string; scanResult?: string }
): FileScanState | undefined => {
  if (!r) return undefined;
  if (r.status === 'infected') return 'infected';
  if (r.scanResult === 'unsupported') return 'unsupported';
  if (r.scanResult === 'scan_failed') return 'scan_failed';
  return undefined;
};

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Step 1: Recipients
  const [title, setTitle] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [message, setMessage] = useState('');
  const [showContactsModal, setShowContactsModal] = useState(false);

  // Step 2: Files
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [dragOver, setDragOver] = useState(false);

  // Step 3: Settings
  const [expiryDays, setExpiryDays] = useState<number | null>(7);
  const [customDate, setCustomDate] = useState('');

  // Trust check
  const { trustResults, loadingEmails, checkEmails, removeEmail: removeTrustEmail } = useTrustCheck();

  // Error handling
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const scanAbortRef = useRef(false);

  // Step navigation
  const nextStep = () => {
    if (currentStep < 3) {
      setError('');
      setCurrentStep((currentStep + 1) as 1 | 2 | 3);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setError('');
      setCurrentStep((currentStep - 1) as 1 | 2 | 3);
    }
  };

  // Recipients handlers — returns invalid emails (empty array = all valid)
  const addRecipients = (input: string): string[] => {
    const emails = input.split(/[,;\s]+/).filter(Boolean);
    if (emails.length === 0) return [];

    const newRecipients = [...recipients];
    const invalidEmails: string[] = [];
    const duplicateEmails: string[] = [];

    for (const raw of emails) {
      const trimmed = raw.trim();
      if (!trimmed) continue;

      if (!isValidEmail(trimmed)) {
        invalidEmails.push(trimmed);
        continue;
      }

      if (newRecipients.includes(trimmed)) {
        duplicateEmails.push(trimmed);
        continue;
      }

      newRecipients.push(trimmed);
    }

    setRecipients(newRecipients);

    // Trigger trust check for newly added valid emails
    const newlyAdded = newRecipients.filter(e => !recipients.includes(e));
    if (newlyAdded.length > 0) {
      checkEmails(newlyAdded);
    }

    // Build error message for invalid/duplicate emails
    const errors: string[] = [];
    if (invalidEmails.length > 0) {
      errors.push(`無効なメールアドレス形式です: ${invalidEmails.join(', ')}`);
    }
    if (duplicateEmails.length > 0) {
      errors.push(`既に追加されています: ${duplicateEmails.join(', ')}`);
    }
    setError(errors.join('　'));

    return invalidEmails;
  };

  const handleRecipientInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const invalidEmails = addRecipients(recipientInput);
      // 無効なメールがあれば入力欄に残す
      setRecipientInput(invalidEmails.length > 0 ? invalidEmails.join(', ') : '');
    }
  };

  const handleRecipientInputBlur = () => {
    if (recipientInput.trim()) {
      const invalidEmails = addRecipients(recipientInput);
      // 無効なメールがあれば入力欄に残す
      setRecipientInput(invalidEmails.length > 0 ? invalidEmails.join(', ') : '');
    }
  };

  const handleNextFromStep1 = () => {
    // 未確定の入力があれば自動確定してから次へ
    if (recipientInput.trim()) {
      const invalidEmails = addRecipients(recipientInput);

      if (invalidEmails.length > 0) {
        setRecipientInput(invalidEmails.join(', '));
        return; // エラーがある場合は遷移しない
      }

      setRecipientInput('');
      // addRecipientsでrecipientsが更新されるが、stateは非同期のためここでは直接計算
      const emails = recipientInput.split(/[,;\s]+/).filter(Boolean);
      const newCount = recipients.length + emails.filter(e => isValidEmail(e.trim()) && !recipients.includes(e.trim())).length;
      if (!title || newCount === 0) return;
      setError('');
      setCurrentStep(2);
    } else {
      nextStep();
    }
  };

  const removeRecipient = (email: string) => {
    setRecipients(recipients.filter(r => r !== email));
    removeTrustEmail(email);
  };

  const handleContactsSelect = (emails: string[]) => {
    const newRecipients = [...recipients];
    const newlyAdded: string[] = [];
    emails.forEach(email => {
      if (!newRecipients.includes(email)) {
        newRecipients.push(email);
        newlyAdded.push(email);
      }
    });
    setRecipients(newRecipients);
    if (newlyAdded.length > 0) {
      checkEmails(newlyAdded);
    }
    setShowContactsModal(false);
  };

  // File handlers
  // Bug-019: 追加後の合計が MAX_FILES を超える場合は上限までのみ追加し、エラーを表示する。
  const addFiles = (selected: File[]) => {
    if (selected.length === 0) return;

    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      setError(`ファイルは最大${MAX_FILES}件まで添付できます`);
      return;
    }

    const accepted = selected.slice(0, remaining).map(file => ({ file, progress: 0 }));
    setFiles([...files, ...accepted]);

    if (selected.length > remaining) {
      setError(`ファイルは最大${MAX_FILES}件まで添付できます（超過した${selected.length - remaining}件は追加されませんでした）`);
    } else {
      setError('');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    if (e.dataTransfer.files) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getTotalSize = (): string => {
    const total = files.reduce((sum, f) => sum + f.file.size, 0);
    return formatFileSize(total);
  };

  // Wait for malware scan completion on all files
  // Bug-018: status='active' でも scanResult が 'unsupported'/'scan_failed'
  // （パスワード付きzip・暗号化ファイル等、GuardDutyがスキャン不能と判定）の場合は
  // ダウンロードできないため、共有作成前に 'unsupported' として弾く。
  // 戻り値: 総合判定（verdict）と、各ファイルの最終スキャン結果（results）。
  // results は Bug-018 でどのファイルが共有不可かを特定するために使用する。
  const waitForScanCompletion = async (
    fileIds: string[]
  ): Promise<{
    verdict: 'clean' | 'infected' | 'unsupported' | 'timeout';
    results: Array<{ fileId: string; status?: string; scanResult?: string }>;
  }> => {
    const POLL_INTERVAL = 2000; // 2 seconds
    const MAX_WAIT = 120000; // 120 seconds
    const startTime = Date.now();

    while (Date.now() - startTime < MAX_WAIT) {
      if (scanAbortRef.current) return { verdict: 'timeout', results: [] };

      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));

      try {
        const results = await Promise.all(
          fileIds.map(async (fileId) => {
            const res = await apiClient.get<FileDetailResponse>(`/api/v1/files/${fileId}`);
            return { fileId, status: res.data.status, scanResult: res.data.scanResult };
          })
        );

        // If any file is infected, abort immediately
        if (results.some(r => r.status === 'infected')) {
          return { verdict: 'infected', results };
        }

        // If all files have finished scanning (active), evaluate the scan result
        if (results.every(r => r.status === 'active')) {
          // スキャン不能（パスワード付きzip等）のファイルが含まれる場合は共有不可
          if (results.some(r => r.scanResult === 'unsupported' || r.scanResult === 'scan_failed')) {
            return { verdict: 'unsupported', results };
          }
          return { verdict: 'clean', results };
        }

        // Otherwise, some files are still scanning — continue polling
      } catch {
        // Polling error — continue trying
        console.warn('[Upload] Scan status polling error, retrying...');
      }
    }

    return { verdict: 'timeout', results: [] };
  };

  // Submit handler
  const handleSubmit = async () => {
    setError('');
    // Bug-018: 再送信時に前回のスキャン警告マークをクリア
    setFiles(prev => prev.map(f => ({ ...f, scanState: undefined })));

    // Validation
    if (!title || title.trim().length === 0) {
      setError('タイトルを入力してください');
      return;
    }

    if (recipients.length === 0) {
      setError('送信先のメールアドレスを入力してください');
      return;
    }

    // Validate all email addresses
    const invalidEmails = recipients.filter(email => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      setError(`無効なメールアドレス形式です: ${invalidEmails.join(', ')}`);
      return;
    }

    if (files.length === 0) {
      setError('ファイルを選択してください');
      return;
    }

    // Bug-019: サーバ側(create-share)上限と同一基準で送信前に弾く（防御的）
    if (files.length > MAX_FILES) {
      setError(`ファイルは最大${MAX_FILES}件まで添付できます`);
      return;
    }

    // 有効期限の算出とバリデーション（アップロード前にクライアント側で実施）
    // サーバ側（create-share）と同一基準: 最大30日、未来日時のみ
    // CR-047: customDate は日付のみ（例 2026-06-20）。タイムゾーンを明示せず
    // new Date(str) するとブラウザのローカルTZ解釈となり、絶対時刻基準のサーバ判定と
    // 境界でずれる。当サービスはJST運用のため「選択日の終端（JST 23:59:59）」を明示する。
    // 日数加算も setDate のローカル依存を排し、ミリ秒基準（now + days*24h）に統一する。
    let expiresAt: Date;
    if (customDate) {
      expiresAt = new Date(customDate + 'T23:59:59+09:00');
      if (isNaN(expiresAt.getTime())) {
        setError('有効期限の日付形式が正しくありません');
        return;
      }
      if (expiresAt <= new Date()) {
        setError('有効期限は現在より未来の日時を指定してください');
        return;
      }
      const maxDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      if (expiresAt > maxDate) {
        setError('有効期限は最大30日後までです');
        return;
      }
    } else {
      expiresAt = new Date(Date.now() + (expiryDays ?? 7) * 24 * 60 * 60 * 1000);
    }

    setIsSubmitting(true);

    try {
      // Upload each file using Pre-signed URL flow
      const uploadPromises = files.map(async (uploadFile) => {
        // Step 1: Get Pre-signed URL from backend
        const uploadUrlRequest: UploadUrlRequest = {
          fileName: uploadFile.file.name,
          fileSize: uploadFile.file.size,
          mimeType: uploadFile.file.type || 'application/octet-stream'
        };

        const urlResponse = await apiClient.post<UploadUrlResponse>('/api/v1/files/upload-url', uploadUrlRequest);

        const { fileId, uploadUrl } = urlResponse.data;

        // Step 2: Upload directly to S3 using Pre-signed URL
        // SSE-KMSの暗号化ヘッダーはPre-signed URLのクエリ文字列に署名済みで含まれるため、
        // ブラウザ側でx-amz-server-side-encryption等のヘッダーを付与してはいけない
        // （クエリ署名と実ヘッダーが二重指定・不一致になり403 Forbiddenとなる）。
        const uploadHeaders: Record<string, string> = {
          'Content-Type': uploadFile.file.type || 'application/octet-stream'
        };

        await fetch(uploadUrl, {
          method: 'PUT',
          body: uploadFile.file,
          headers: uploadHeaders
        });

        // Step 3: Notify backend that upload is complete
        await apiClient.post(`/api/v1/files/${fileId}/complete`);

        return fileId;
      });

      const fileIds = await Promise.all(uploadPromises);

      // Wait for malware scanning to complete
      setIsScanning(true);
      scanAbortRef.current = false;

      const scan = await waitForScanCompletion(fileIds);

      setIsScanning(false);

      if (scanAbortRef.current) {
        setIsSubmitting(false);
        return;
      }

      // Bug-018: スキャンで問題のあったファイルを特定し、ファイル一覧（ステップ2）へ戻して
      // 該当ファイルに警告マーク＋色付けを行い、「どのファイルが該当したか／削除が必要」を可視化する。
      if (scan.verdict === 'infected' || scan.verdict === 'unsupported') {
        const byId = new Map(scan.results.map(r => [r.fileId, r]));
        setFiles(prev => prev.map((f, i) => ({
          ...f,
          fileId: fileIds[i],
          scanState: deriveScanState(byId.get(fileIds[i])),
        })));

        setError(
          scan.verdict === 'infected'
            ? 'マルウェアが検出されたファイルがあります。警告マークの付いたファイルを削除してください。'
            : 'セキュリティスキャンができないファイルがあります（パスワード付きzip・暗号化ファイル等）。警告マークの付いたファイルを削除してください。'
        );
        setCurrentStep(2); // ファイル一覧へ戻して該当ファイルを表示
        setIsSubmitting(false);
        return;
      }

      if (scan.verdict === 'timeout') {
        setError('ウイルススキャンに時間がかかっています。後ほどファイル一覧から共有を作成してください。');
        setIsSubmitting(false);
        return;
      }

      // Create share with all uploaded files（有効期限は送信前に算出・検証済み）
      const shareRequest: ShareRequest = {
        fileIds,
        title: title.trim(),
        recipients,
        expiresAt: expiresAt.toISOString(),
        message: message.trim() || undefined
      };

      await apiClient.post<ShareResponse>('/api/v1/shares', shareRequest);

      // Show completion dialog
      setIsSubmitting(false);
      setShowCompleteDialog(true);
    } catch (err: unknown) {
      // Bug-018: create-share がスキャン不能ファイル(FILE_SHARE_NOT_SCANNABLE)等を弾いた場合、
      // 既知のエラーコードは個別メッセージを表示する（防御的に二重で弾く）。
      const code = axios.isAxiosError(err) ? err.response?.data?.error?.code : undefined;
      const messages = ERROR_MESSAGES || {};
      setError(code && messages[code] ? messages[code] : 'アップロードに失敗しました。もう一度お試しください。');
      setIsSubmitting(false);
      setIsScanning(false);
    }
  };

  return (
    <>
      <Header />
      {/* Container */}
      <div className="page-wrapper">
        <div className="page-container page-container--narrow">
          {/* Progress Steps */}
          <div className="steps">
            <div className="steps__line" />

            {[1, 2, 3].map((step) => (
              <div key={step} className="steps__item">
                <div className={`steps__circle ${
                  currentStep === step ? 'steps__circle--current' :
                  currentStep > step ? 'steps__circle--completed' :
                  'steps__circle--pending'
                }`}>
                  {step}
                </div>
                <div className={`steps__label ${currentStep === step ? 'steps__label--current' : ''}`}>
                  {step === 1 ? '送信先' : step === 2 ? 'ファイル選択' : '設定・確認'}
                </div>
              </div>
            ))}
          </div>

          {/* Upload Card */}
          <div className="card">
            {/* Step 1: Recipients */}
            {currentStep === 1 && (
              <>
                <div className="card__header">
                  <h2>ステップ1: 送信先を指定</h2>
                  <p className="text-muted text-sm">
                    送信するファイルのタイトルと、送信先のメールアドレスを入力してください
                  </p>
                </div>

                <div className="card__body">
                  {/* Title */}
                  <div className="form-group">
                    <label className="form-label form-label--required">タイトル</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="例: 2026年度 事業計画書"
                      className="form-input"
                    />
                  </div>

                  {/* Recipients */}
                  <div className="form-group">
                    <label className="form-label form-label--required">受信者</label>
                    <input
                      type="email"
                      value={recipientInput}
                      onChange={(e) => setRecipientInput(e.target.value)}
                      onKeyDown={handleRecipientInputKeyDown}
                      onBlur={handleRecipientInputBlur}
                      placeholder="メールアドレスを入力（Enter または カンマで区切り）"
                      className="form-input"
                    />
                    <button
                      onClick={() => setShowContactsModal(true)}
                      className="btn btn--secondary btn--sm mt-4"
                    >
                      <span className="material-symbols-outlined icon-sm">group</span> 連絡先から選択
                    </button>

                    {/* Recipient chips */}
                    {recipients.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {recipients.map((email) => {
                          const trustResult = trustResults.get(email);
                          return (
                            <RecipientChip
                              key={email}
                              email={email}
                              onRemove={removeRecipient}
                              trustLevel={trustResult?.trustLevel}
                              displayName={trustResult?.displayName}
                              isLoading={loadingEmails.has(email)}
                            />
                          );
                        })}
                      </div>
                    )}

                    {error && (
                      <div className="alert alert--error mt-4">
                        {error}
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div className="form-group">
                    <label className="form-label">メッセージ（オプション）</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="受信者へのメッセージを入力してください"
                      className="form-textarea"
                    />
                  </div>

                  {/* Navigation */}
                  <div className="card__footer flex justify-between gap-3 mt-6">
                    <button
                      disabled
                      className="btn btn--secondary"
                    >
                      ← 前へ
                    </button>
                    <button
                      onClick={handleNextFromStep1}
                      disabled={!title || (recipients.length === 0 && !recipientInput.trim())}
                      className="btn btn--primary"
                    >
                      次へ: ファイル選択 →
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: File Upload */}
            {currentStep === 2 && (
              <>
                <div className="card__header">
                  <h2>ステップ2: ファイルを選択</h2>
                  <p className="text-muted text-sm">
                    送信するファイルを選択してください（1ファイル最大5GB・最大{MAX_FILES}件）
                  </p>
                </div>

                <div className="card__body">
                  {/* Drop Zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`dropzone ${dragOver ? 'dropzone--active' : ''}`}
                  >
                    <div style={{ marginBottom: '16px' }}><span className="material-symbols-outlined" style={{ fontSize: '48px' }}>upload_file</span></div>
                    <h3 className="mb-2">ファイルをここにドロップ</h3>
                    <p className="text-muted text-sm mb-4">または</p>
                    <label>
                      <input
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <span className="btn btn--primary">
                        ファイルを選択
                      </span>
                    </label>
                  </div>

                  {/* Selected Files */}
                  {files.length > 0 && (
                    <div className="flex flex-col gap-3 mt-6">
                      {files.map((uploadFile, index) => {
                        // Bug-018: スキャン不能/マルウェア検出ファイルは警告表示し削除を促す
                        const hasIssue = uploadFile.scanState !== undefined;
                        const issueLabel = uploadFile.scanState === 'infected'
                          ? 'マルウェアが検出されました。削除してください'
                          : 'セキュリティスキャンができません。削除してください';
                        return (
                          <div key={index} className={`file-item${hasIssue ? ' file-item--error' : ''}`}>
                            <div className="file-item__info">
                              <span className="file-item__icon">
                                {hasIssue ? (
                                  <span className="material-symbols-outlined" style={{ color: 'var(--color-danger)' }} aria-label="共有不可" title={issueLabel}>warning</span>
                                ) : (
                                  <span className="material-symbols-outlined">
                                    {uploadFile.file.type.startsWith('image/') ? 'image' :
                                     uploadFile.file.type.includes('pdf') ? 'description' :
                                     uploadFile.file.type.includes('sheet') || uploadFile.file.type.includes('excel') ? 'bar_chart' :
                                     uploadFile.file.type.includes('word') || uploadFile.file.type.includes('document') ? 'edit_note' : 'attach_file'}
                                  </span>
                                )}
                              </span>
                              <div className="flex-1">
                                <div className="file-item__name">{uploadFile.file.name}</div>
                                <div className="file-item__size">{formatFileSize(uploadFile.file.size)}</div>
                                {hasIssue && (
                                  <div className="file-item__warning">
                                    <span className="material-symbols-outlined icon-sm">error</span>
                                    {issueLabel}
                                  </div>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => removeFile(index)}
                              className="btn btn--outline-danger btn--sm"
                            >
                              削除
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {error && (
                    <div className="alert alert--error mt-4">
                      {error}
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="card__footer flex justify-between gap-3 mt-6">
                    <button
                      onClick={prevStep}
                      className="btn btn--secondary"
                    >
                      ← 前へ: 送信先
                    </button>
                    <button
                      onClick={nextStep}
                      disabled={files.length === 0}
                      className="btn btn--primary"
                    >
                      次へ: 設定・確認 →
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Settings & Confirmation */}
            {currentStep === 3 && (
              <>
                <div className="card__header">
                  <h2>ステップ3: 設定と確認</h2>
                  <p className="text-muted text-sm">
                    有効期限を設定し、送信内容を確認してください
                  </p>
                </div>

                <div className="card__body">
                  {/* Expiry Options */}
                  <div className="form-group">
                    <label className="form-label">有効期限</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
                      {[1, 7, 30].map((days) => (
                        <label key={days} className="expiry-option">
                          <input
                            type="radio"
                            name="expiry"
                            value={days}
                            checked={expiryDays === days}
                            onChange={() => { setExpiryDays(days); setCustomDate(''); }}
                          />
                          <div className="expiry-option__label">
                            {days}日
                          </div>
                        </label>
                      ))}
                    </div>
                    <div
                      className={`expiry-custom${customDate ? ' expiry-custom--active' : ''}`}
                      onClick={() => {
                        const dateInput = document.getElementById('custom-expiry-date') as HTMLInputElement;
                        dateInput?.focus();
                      }}
                    >
                      <span className="material-symbols-outlined icon-sm" style={{ marginRight: '8px' }}>calendar_month</span>
                      <span style={{ marginRight: '8px' }}>日付を指定:</span>
                      <input
                        id="custom-expiry-date"
                        type="date"
                        value={customDate}
                        onChange={(e) => { setCustomDate(e.target.value); setExpiryDays(e.target.value ? null : 7); }}
                        min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                        max={new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]}
                        className="expiry-custom__input"
                      />
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="card mb-4" style={{ background: 'var(--color-surface-hover)' }}>
                    <div className="card__body">
                      <div className="font-semibold mb-3"><span className="material-symbols-outlined icon-sm">checklist</span> 送信内容の確認</div>
                      <div className="summary-row">
                        <span className="summary-row__label">タイトル:</span>
                        <span className="summary-row__value">{title}</span>
                      </div>
                      <div className="summary-row">
                        <span className="summary-row__label">受信者:</span>
                        <span className="summary-row__value">
                          {(() => {
                            const hasLevel4 = recipients.some(e => trustResults.get(e)?.trustLevel === 4);
                            const allLevel3or4 = recipients.length > 0 && recipients.every(e => {
                              const level = trustResults.get(e)?.trustLevel;
                              return level === 3 || level === 4;
                            });
                            return (
                              <div className={allLevel3or4 ? 'trust-highlight-recipients' : ''}>
                                <div className="flex flex-wrap gap-2">
                                  {recipients.map((email) => {
                                    const trustResult = trustResults.get(email);
                                    return (
                                      <RecipientChip
                                        key={email}
                                        email={email}
                                        onRemove={removeRecipient}
                                        trustLevel={trustResult?.trustLevel}
                                        displayName={trustResult?.displayName}
                                        isLoading={loadingEmails.has(email)}
                                      />
                                    );
                                  })}
                                </div>
                                {hasLevel4 && (
                                  <div className="trust-warning-banner mt-4" data-testid="trust-warning-banner">
                                    <span className="material-symbols-outlined">warning</span>
                                    初めてのドメインへの送信が含まれています
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </span>
                      </div>
                      <div className="summary-row">
                        <span className="summary-row__label">ファイル数:</span>
                        <span className="summary-row__value">{files.length}ファイル</span>
                      </div>
                      <div className="summary-row">
                        <span className="summary-row__label">合計サイズ:</span>
                        <span className="summary-row__value">{getTotalSize()}</span>
                      </div>
                      <div className="summary-row">
                        <span className="summary-row__label">有効期限:</span>
                        <span className="summary-row__value">{customDate ? customDate : `${expiryDays}日`}</span>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="alert alert--error">
                      {error}
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="card__footer flex justify-between gap-3 mt-6">
                    <button
                      onClick={prevStep}
                      disabled={isSubmitting}
                      className="btn btn--secondary"
                    >
                      ← 前へ: ファイル選択
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="btn btn--primary"
                    >
                      {isScanning ? 'ウイルススキャン中...' : isSubmitting ? '送信中...' : <><span className="material-symbols-outlined icon-sm">upload</span> ファイルを送信</>}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Contacts Modal */}
      <ContactsModal
        isOpen={showContactsModal}
        onClose={() => setShowContactsModal(false)}
        onSelect={handleContactsSelect}
      />

      {/* Completion Dialog */}
      <MessageDialog
        isOpen={showCompleteDialog}
        type="success"
        title="送信完了"
        message={`ファイルの送信が完了しました。\n受信者にメール通知が送信されます。`}
        onClose={() => navigate('/files')}
      />
    </>
  );
};

export default Upload;
